/**
 * The rider's photos in the ERP (migration 196; the owner, 2026-09-30: "the image should also be shown on the erp").
 *
 * The bytes live in the private R2 bucket behind the photo Worker. The browser asks the Worker for each one with the
 * person's own session token; the Worker asks the ERP (api.delivery_photo_access) before it streams anything, so a
 * photo is seen only by someone with delivery.view. The ERP lists the day's photos with the event that named each —
 * whose duty, which door, what happened there (api.delivery_photos).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageOff } from 'lucide-react';
import { rpc, supabase } from '../../lib/supabase';
import { Panel, PanelHeader, cx } from '../../ui/primitives';
import { Dialog } from '../../ui/overlays';
import { time as fmtTime } from '../../ui/format';

export interface DeliveryPhotoRow { key: string; kind: string; serviceDate: string; riderCode: string; riderName: string | null; uploadedAt: string;
  event: string | null; takenAt: string | null; customerCode: string | null; customerName: string | null; tripCode: string | null }

const WORKER = (import.meta.env.VITE_PHOTO_WORKER_URL as string | undefined) ?? 'https://rtcom-delivery-photos.innovations-desigomilk.workers.dev';
const KIND: Record<string, string> = { selfie: 'Duty selfie', gate: 'Gate', doorstep: 'Doorstep', problem: 'Problem', pod: 'Proof of delivery' };
const time = (iso: string | null) => (iso ? fmtTime(iso) : '—');

/** One photo, fetched with the person's own token; a failure says why instead of showing a broken image. */
export function DeliveryPhoto({ photoKey, className }: { photoKey: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let live = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${WORKER}/v2/photo/${photoKey.split('/').map(encodeURIComponent).join('/')}`,
        { headers: { Authorization: `Bearer ${data.session?.access_token ?? ''}` } });
      if (!res.ok) throw new Error(res.status === 404 ? 'not found (is the photo Worker’s viewer deployed?)' : res.status === 403 ? 'not allowed' : `HTTP ${res.status}`);
      url = URL.createObjectURL(await res.blob());
      if (live) setSrc(url);
    })().catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [photoKey]);
  if (error) return <div className={cx('flex items-center justify-center gap-1 bg-surface-sunken p-2 text-center text-xs text-ink-muted', className)} title={photoKey}><ImageOff size={14} /> {error}</div>;
  if (!src) return <div className={cx('animate-pulse bg-surface-sunken', className)} />;
  return <img src={src} alt={photoKey} className={cx('object-cover', className)} />;
}

/** A rider's voice note (migration 133800: voice/{date}/{RID}/{uuid}.m4a), fetched like a photo and played inline. */
export function DeliveryVoice({ voiceKey, durationMs, className }: { voiceKey: string; durationMs?: number | null; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let live = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${WORKER}/v2/photo/${voiceKey.split('/').map(encodeURIComponent).join('/')}`,
        { headers: { Authorization: `Bearer ${data.session?.access_token ?? ''}` } });
      if (!res.ok) throw new Error(res.status === 404 ? 'the voice note is not stored yet' : res.status === 403 ? 'not allowed' : `HTTP ${res.status}`);
      url = URL.createObjectURL(await res.blob());
      if (live) setSrc(url);
    })().catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [voiceKey]);
  const secs = durationMs != null ? `${Math.round(durationMs / 1000)} s` : '';
  if (error) return <span className={cx('text-xs text-ink-muted', className)} title={voiceKey}>Voice note {secs}: {error}</span>;
  if (!src) return <span className={cx('text-xs text-ink-muted', className)}>Voice note {secs}…</span>;
  return <audio controls preload="none" src={src} className={cx('h-8 max-w-full', className)} aria-label={`Voice note ${secs}`} />;
}

/** The day's photos, optionally one rider's or one kind's, as a grid of thumbnails that open large. */
export function DeliveryPhotosPanel({ date, riderCode, kind, title = 'Photos from the phone' }: { date: string; riderCode?: string | null; kind?: string; title?: string }) {
  const q = useQuery({ queryKey: ['delivery-photos', date, riderCode ?? null, kind ?? null], refetchInterval: 60_000,
    queryFn: () => rpc<DeliveryPhotoRow[]>('delivery_photos', { p_date: date, p_rider_code: riderCode ?? null, p_kind: kind ?? null }) });
  const [open, setOpen] = useState<DeliveryPhotoRow | null>(null);
  const rows = q.data ?? [];
  return (
    <Panel>
      <PanelHeader title={title} note={q.isLoading ? 'Reading…' : `${rows.length} photo(s). Photos are taken for a duty selfie, a door with no QR, the gate photo, and a delivery marked by hand.`} />
      {q.error ? <p className="px-3 py-2 text-sm text-refused">{(q.error as Error).message}</p>
        : rows.length === 0 ? <p className="px-3 py-3 text-sm text-ink-muted">No photo on this day.</p>
        : (
          <ul className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-4 lg:grid-cols-6">
            {rows.map((p) => (
              <li key={p.key}>
                <button type="button" className="block w-full text-left" onClick={() => setOpen(p)}>
                  <DeliveryPhoto photoKey={p.key} className="h-28 w-full rounded" />
                  <span className="mt-1 block text-xs font-medium">{KIND[p.kind] ?? p.kind} · {time(p.takenAt ?? p.uploadedAt)}</span>
                  <span className="block truncate text-xs text-ink-muted">{p.riderCode}{p.customerCode ? ` · ${p.customerCode}` : ''}</span>
                </button>
              </li>))}
          </ul>)}
      <Dialog open={!!open} onClose={() => setOpen(null)} title={open ? `${KIND[open.kind] ?? open.kind} — ${open.riderCode}${open.riderName ? ` · ${open.riderName}` : ''}` : ''}>
        {open && (
          <div className="grid gap-2">
            <DeliveryPhoto photoKey={open.key} className="max-h-[70vh] w-full rounded object-contain" />
            <p className="text-sm text-ink-muted">
              {open.customerCode ? `${open.customerCode}${open.customerName ? ` · ${open.customerName}` : ''} · ` : ''}
              {open.tripCode ? `${open.tripCode} · ` : ''}{open.event ? `${open.event.replace(/_/g, ' ')} · ` : ''}taken {time(open.takenAt)} · received {time(open.uploadedAt)}
            </p>
          </div>)}
      </Dialog>
    </Panel>
  );
}

/** Photos over a range, for one rider or one customer (api.delivery_photos_between, migration 203). */
export function DeliveryPhotosRange({ from, to, riderCode, customerCode, title = 'Photos' }:
  { from: string; to: string; riderCode?: string | null; customerCode?: string | null; title?: string }) {
  const q = useQuery({ queryKey: ['delivery-photos-range', from, to, riderCode ?? null, customerCode ?? null], refetchInterval: 120_000,
    queryFn: () => rpc<DeliveryPhotoRow[]>('delivery_photos_between', { p_from: from, p_to: to, p_rider_code: riderCode ?? null, p_customer_code: customerCode ?? null, p_kind: null }) });
  const [open, setOpen] = useState<DeliveryPhotoRow | null>(null);
  const rows = q.data ?? [];
  return (
    <Panel>
      <PanelHeader title={title} note={q.isLoading ? 'Reading…' : `${rows.length} photo(s), newest first.`} />
      {q.error ? <p className="px-3 py-2 text-sm text-refused">{(q.error as Error).message}</p>
        : rows.length === 0 ? <p className="px-3 py-3 text-sm text-ink-muted">No photo in this period.</p>
        : (
          <ul className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-4 lg:grid-cols-6">
            {rows.map((p) => (
              <li key={p.key}>
                <button type="button" className="block w-full text-left" onClick={() => setOpen(p)}>
                  <DeliveryPhoto photoKey={p.key} className="h-28 w-full rounded" />
                  <span className="mt-1 block text-xs font-medium">{KIND[p.kind] ?? p.kind} · {p.serviceDate} {time(p.takenAt ?? p.uploadedAt)}</span>
                  <span className="block truncate text-xs text-ink-muted">{p.riderCode}{p.customerCode ? ` · ${p.customerCode}` : ''}</span>
                </button>
              </li>))}
          </ul>)}
      <Dialog open={!!open} onClose={() => setOpen(null)} title={open ? `${KIND[open.kind] ?? open.kind} — ${open.serviceDate} · ${open.riderCode}` : ''}>
        {open && <div className="grid gap-2"><DeliveryPhoto photoKey={open.key} className="max-h-[70vh] w-full rounded object-contain" />
          <p className="text-sm text-ink-muted">{open.customerCode ? `${open.customerCode}${open.customerName ? ` · ${open.customerName}` : ''} · ` : ''}{open.tripCode ? `${open.tripCode} · ` : ''}taken {time(open.takenAt)} · received {time(open.uploadedAt)}</p></div>}
      </Dialog>
    </Panel>
  );
}
